/**
 * @fileoverview Feature Flags Hook
 * 
 * Fetches feature flag states from the database and provides
 * a helper to check if a feature is enabled. Defaults all flags
 * to true while loading to prevent flicker.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type FeatureFlagId = 'try_on' | 'polls' | 'studio' | 'chat' | 'style_interview' | 'body_gym' | 'trend_insights' | 'price_guess' | 'daily_outfit' | 'salon';

interface FeatureFlag {
  id: string;
  label: string;
  description: string | null;
  is_enabled: boolean;
  updated_at: string;
}

export function useFeatureFlags() {
  const { data: flags = [], isLoading } = useQuery({
    queryKey: ['feature-flags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('*');
      if (error) throw error;
      return data as FeatureFlag[];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 15,
  });

  const flagMap = new Map(flags.map(f => [f.id, f.is_enabled]));

  const isEnabled = (flagId: FeatureFlagId): boolean => {
    if (isLoading) return true; // Default to enabled while loading
    return flagMap.get(flagId) ?? true;
  };

  return { flags, isEnabled, isLoading };
}
